import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {IconEyeComponent} from '@components/icons/icon-eye/icon-eye.component';
import {IconLocationAddComponent} from '@components/icons/icon-location-add/icon-location-add.component';
import {DemandaAcaoGuardService} from '@core/guards/demanda-acao.guard';
import {Demanda} from '@core/models/demanda';
import {PageProps} from '@core/models/pagination';
import {DemandaService, Filter} from '@core/services/demanda.service';
import {
    VisualizarReforcarDemandaDialog
} from '@pages/demandas/visualizar-reforcar-demanda-dialog/visualizar-reforcar-demanda-dialog.component';
import {DatePtbrPipe} from '@shared-pipes/date-format.pipe';
import {MessageService, TreeNode} from 'primeng/api';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DatePickerModule} from 'primeng/datepicker';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {SelectModule} from 'primeng/select';
import {Skeleton} from 'primeng/skeleton';
import {SliderModule} from 'primeng/slider';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {TagModule} from 'primeng/tag';
import {ToastModule} from 'primeng/toast';
import {TooltipModule} from 'primeng/tooltip';
import {TreeTable, TreeTableModule, TreeTableNodeExpandEvent} from 'primeng/treetable';
import {TableSortResetter} from '@shared/utils/table-sort-resetter';
import {debounceTime, distinctUntilChanged, Subject, Subscription,} from 'rxjs';
import {map} from 'rxjs/operators';

interface SituacaoOption {
    label: string;
    value: number;
}

type FilterValue = string | number | null;

@Component({
    selector: 'app-tabela-demandas',
    standalone: true,
    imports: [
        IconFieldModule,
        InputIconModule,
        MultiSelectModule,
        TagModule,
        ButtonModule,
        SliderModule,
        SelectModule,
        RouterModule,
        AutoCompleteModule,
        FormsModule,
        InputTextModule,
        TooltipModule,
        CardModule,
        DatePtbrPipe,
        CommonModule,
        ToastModule,
        TreeTableModule,
        Skeleton,
        TableModule,
        IconLocationAddComponent,
        IconEyeComponent,
        VisualizarReforcarDemandaDialog,
        DatePickerModule,
    ],
    providers: [DemandaService, MessageService],
    templateUrl: './tabela-demandas.component.html',
    styleUrl: './tabela-demandas.component.css',
})
export class TabelaDemandasComponent implements OnInit, OnDestroy {
    @ViewChild('dt') dt!: TreeTable;

    demandas: Demanda[] = [];
    demandaSelecionadaId: number | null = null;
    loading: boolean = false;
    totalRecords = 0;
    dataTree: TreeNode[] = [];
    situacaoOptions: SituacaoOption[] = [];
    loadingLeafTable: boolean = false;
    showVisualizarDemandaDialog = false;
    showReforcarDemandaDialog = false;
    protected currentFilters: { [key: string]: FilterValue } = {};
    private currentPage = 0;
    private currentSize = 10;
    private readonly subscriptions: Subscription[] = [];
    private currentSortField: string | null | undefined = undefined;
    private currentSortDirection: string | null | undefined = undefined;
    private readonly sortResetter = new TableSortResetter();
    private readonly filterSubjects: { [key: string]: Subject<FilterValue> } = {
        protocolo: new Subject<FilterValue>(),
        tipoObra: new Subject<FilterValue>(),
        autor: new Subject<FilterValue>(),
        representacao: new Subject<FilterValue>(),
        data: new Subject<FilterValue>(),
        situacao: new Subject<FilterValue>(),
    };

    private readonly demandaAcaoGuardService = inject(DemandaAcaoGuardService);

    constructor(
        private readonly DemandaService: DemandaService,
        private readonly messageService: MessageService,
    ) {
    }

    ngOnInit() {
        this.carregarSituacoes();
        this.subscribeFilterValueChanges();
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
    }

    carregarSituacoes() {
        this.DemandaService.listarSituacoesDemanda()
            .pipe(
                map((data) =>
                    data.map((item) => ({
                        label: item.nomeSituacao,
                        value: item.id,
                    })),
                ),
            )
            .subscribe({
                next: (options) => {
                    this.situacaoOptions = [{label: 'Todos', value: null}, ...options];
                },
                error: (error) => {
                    this.situacaoOptions = [];
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao carregar opções de situação.',
                        life: 3000,
                    });
                },
            });
    }

    onClickVisualizar(demandaId: number) {
        this.demandaSelecionadaId = demandaId;
        this.showVisualizarDemandaDialog = true;
    }

    onClickReforcar(demandaId: number) {
        if (!this.demandaAcaoGuardService.canProceed('reforcar')) return;
        this.demandaSelecionadaId = demandaId;
        this.showReforcarDemandaDialog = true;
    }

    onReforcarDemandaSuccess(): void {
        this.getDemandas(this.currentPage, this.currentSize, this.currentSortField, this.currentSortDirection, this.currentFilters);
    }

    getDemandas(
        page: number,
        size: number,
        sortField: string | null | undefined,
        sortDirection: string | null | undefined,
        filters: { [key: string]: FilterValue },
    ): void {
        this.loading = true;
        this.dataTree = [];
        const filterArray: Filter[] = [];

        for (const key in filters) {
            if (
                Object.hasOwn(filters, key) &&
                filters[key] !== null &&
                filters[key] !== undefined &&
                String(filters[key]).trim() !== ''
            ) {
                filterArray.push({field: key, value: filters[key]});
            }
        }

        const {sortField: sortFieldToUse, sortDirection: sortDirectionToUse} =
            this.resolveSortParams(sortField, sortDirection, filterArray);

        this.DemandaService.listarDemandas(
            page,
            size,
            sortFieldToUse,
            sortDirectionToUse,
            filterArray,
        ).subscribe({
            next: (response: PageProps<Demanda>) => {
                this.demandas = response.content.map((demanda) => ({
                    ...demanda,
                    reforcos: undefined,
                }));
                this.convertDataToTreeNode(this.demandas);
                this.totalRecords = response.totalElements;
                this.loading = false;
            },
            error: (error: unknown) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro!',
                    detail: 'Falha ao carregar as demandas.',
                    life: 5000,
                });
                this.loading = false;
            },
        });

        this.currentPage = page;
        this.currentSize = size;
        this.currentSortField = sortField;
        this.currentSortDirection = sortDirection;
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        this.currentPage = (event.first ?? 0) / (event.rows ?? 10);
        this.currentSize = event.rows ?? 10;

        let incomingField: string | undefined;
        if (typeof event.sortField === 'string') {
            incomingField = event.sortField;
        } else if (Array.isArray(event.sortField) && event.sortField.length > 0) {
            incomingField = event.sortField[0];
        }
        const incomingOrder = event.sortOrder ?? 0;

        if (this.sortResetter.reset(incomingField, incomingOrder)) {
            this.dt.sortField = null;
            this.dt.sortOrder = 0;
            this.currentSortField = undefined;
            this.currentSortDirection = undefined;
            this.getDemandas(this.currentPage, this.currentSize, undefined, undefined, this.currentFilters);
            return;
        }

        this.currentSortField = incomingField;
        if (incomingOrder === 1) {
            this.currentSortDirection = 'asc';
        } else if (incomingOrder === -1) {
            this.currentSortDirection = 'desc';
        } else {
            this.currentSortDirection = null;
        }

        this.getDemandas(
            this.currentPage,
            this.currentSize,
            this.currentSortField,
            this.currentSortDirection,
            this.currentFilters,
        );
    }

    subscribeFilterValueChanges() {
        for (const key of Object.keys(this.filterSubjects)) {
            const subscription = this.filterSubjects[key]
                .pipe(debounceTime(300), distinctUntilChanged())
                .subscribe((query) => {
                    if (
                        query !== null &&
                        query !== undefined &&
                        String(query).trim() !== ''
                    ) {
                        this.currentFilters[key] = query;
                    } else {
                        delete this.currentFilters[key];
                    }

                    this.currentPage = 0;
                    if (this.dt) {
                        this.dt.first = 0;
                    }
                    this.getDemandas(
                        this.currentPage,
                        this.currentSize,
                        this.currentSortField,
                        this.currentSortDirection,
                        this.currentFilters,
                    );
                });
            this.subscriptions.push(subscription);
        }
    }

    onFilterInput(
        eventOrValue: Event | { target: { value: FilterValue } } | FilterValue | Date,
        field: string,
    ) {
        let value: FilterValue | Date;

        if (eventOrValue instanceof Event) {
            value = (eventOrValue.target as HTMLInputElement | null)?.value ?? null;
        } else if (eventOrValue instanceof Date) {
            value = eventOrValue;
        } else if (typeof eventOrValue === 'object' && eventOrValue !== null && 'target' in eventOrValue) {
            value = eventOrValue.target.value;
        } else {
            value = eventOrValue;
        }

        if (value instanceof Date) {
            if (field !== 'data') return;
            value = this.formatDate(value);
        }

        this.filterSubjects[field].next(value);
    }

    convertDataToTreeNode(data: Demanda[]) {
        this.dataTree = data.map((demanda) => ({
            key: demanda.id?.toString(),
            data: {
                id: demanda.id,
                protocolo: demanda.protocolo,
                tipoObra: demanda.tipoObra,
                autor: demanda.autor,
                tipoRepresentacao: demanda.tipoRepresentacao,
                data: demanda.data,
                situacao: demanda.situacao,
            },
            leaf: !demanda.possuiReforco,
            disabled: !demanda.possuiReforco,
            children: demanda.reforcos
                ? demanda.reforcos.map((reforco) => ({
                    key: reforco.id?.toString(),
                    data: {
                        id: reforco.id,
                        protocolo: reforco.protocolo,
                        tipoObra: reforco.tipoObra,
                        autor: reforco.autor,
                        tipoRepresentacao: reforco.tipoRepresentacao,
                        data: reforco.data,
                        situacao: reforco.situacao,
                    },
                    leaf: true,
                }))
                : [],
        }));
    }

    onNodeExpand(event: TreeTableNodeExpandEvent): void {
        const node = event.node;
        const demandaId = Number(node.key);

        if (node.children && node.children.length > 0) {
            node.expanded = true;
            return;
        }

        this.loadingLeafTable = true;

        this.DemandaService.listarHistoricoReforcosPorDemandaId(
            demandaId,
        ).subscribe({
            next: (reforcos) => {
                const childrenNodes = reforcos.map((reforco) => ({
                    key: reforco.id?.toString(),
                    data: {
                        id: reforco.id,
                        protocolo: reforco.protocolo,
                        tipoObra: reforco.tipoObra,
                        autor: reforco.autor,
                        tipoRepresentacao: reforco.tipoRepresentacao,
                        data: reforco.data,
                        situacao: reforco.situacao,
                    },
                    leaf: true,
                }));

                this.dataTree = this.dataTree.map((rootNode) => {
                    if (rootNode.key === node.key) {
                        return {
                            ...rootNode,
                            children: childrenNodes,
                            expanded: true,
                        };
                    }
                    return rootNode;
                });

                this.loadingLeafTable = false;
            },
            error: (err) => {
                this.loadingLeafTable = false;
                this.showReforcoLoadError(err);
            },
        });
    }

    clearFilter(field: string): void {
        this.currentFilters[field] = null;
        this.onFilterInput(null, field);
    }

    private formatDate(date: Date): string {
        const datePipe = new DatePtbrPipe();
        return datePipe.transform(date);
    }

    private resolveSortParams(
        sortField: string | null | undefined,
        sortDirection: string | null | undefined,
        filters: Filter[],
    ): {
        sortField: string | null | undefined;
        sortDirection: string | null | undefined;
    } {
        const hasUserSort =
            !!sortField && (sortDirection === 'asc' || sortDirection === 'desc');
        if (filters.length === 0 && !hasUserSort) {
            return {sortField: 'demanda.id', sortDirection: 'desc'};
        }
        return {sortField, sortDirection};
    }

    private showReforcoLoadError(err: unknown): void {
        let errorMessage = 'Falha ao carregar os reforços da demanda.';
        if (
            err instanceof HttpErrorResponse &&
            err.error &&
            typeof err.error === 'object' &&
            'message' in err.error
        ) {
            errorMessage = err.error.message;
        }

        this.messageService.add({
            severity: 'error',
            summary: 'Erro!',
            detail: errorMessage,
            life: 5000,
        });
    }
}
